import List from '@/models/List';
import { Workshop } from '@/models/Workshop';
import { WorkshopMeeting } from '@/models/WorkshopMeeting';
import { LocaleData } from '@/translations/LocaleData';
import { areIntervalsOverlapping, isBefore, isSameDay } from 'date-fns';
import _ from 'underscore';

/**
 * Compare two meetings and verifies if haves any date overlap in that day
 * @param {WorkshopMeeting} meet1 Meeting to be compared
 * @param {WorkshopMeeting} meet2 Previous meeting to be compared
 * @returns {boolean} true if haves any overlap, false otherwise
 */
const haveDateOverlap = (meet1: WorkshopMeeting, meet2: WorkshopMeeting) => {
    if (isSameDay(meet1.startDate, meet2.startDate)) {
        const range1 = { start: meet1.startDate, end: meet1.endDate };
        const range2 = { start: meet2.startDate, end: meet2.endDate };

        // Exists an overlap
        return areIntervalsOverlapping(range1, range2);
    }

    return false;
};

/**
 * Compare two meetings and verify if haves an overlap or order conflict
 * @param {WorkshopMeeting} meet  The meet to compare
 * @param {WorkshopMeeting} previousMeeting The other workshop with a lowest order
 * @return {boolean} true if any kind of conflict was found between the two meetings
 */
const meetingsHaveConflict = (meet: WorkshopMeeting, previousMeeting: WorkshopMeeting) => {
    if (haveDateOverlap(meet, previousMeeting)) return true;
    return isBefore(meet.startDate, previousMeeting.startDate);
};

/**
 * Verifies if a workshop have a wrong order of the meetings or meetings overlap
 * @param {Workshop[]} workshops A list of workshops with it's meetings
 * @returns {boolean} true if haves any conflict between any workshop
 */
const findConflicts = (workshops: Workshop[], courses: List[]) => {
    const size = workshops.length;

    workshops.sort((w1: Workshop, w2: Workshop) => compareWorkshopListByCourseOrder(w1, w2, courses));

    // Loop through all workshops in reverse order
    for (let i = size - 1; i > 0; i--) {
        const meetings = workshops[i].meetings;

        /* Loops through the list of Workshop[i] meetings and look for inconsistency in its order
        or dates overlaps in relation to the previous workshops */
        for (const meet1 of meetings) {
            // Loop through the others workshops
            for (let x = i - 1; x >= 0; x--) {
                const previousMeetings = workshops[x].meetings;
                // Compares the current meet from workshop[i] with all meets of workshop[x]
                const hasConflict = previousMeetings.some((meet2) => meetingsHaveConflict(meet1, meet2));

                if (hasConflict) return true;
            }
        }
    }

    return false;
};

/**
 * Verifies if a workshop have any kind of conflict
 * @param {Workshop[]} workshops A list of workshops
 * @returns {boolean} true if haves any conflict between workshops, returns false otherwise.
 */
const programHasWorkshopConflict = (workshops: Workshop[], courses: List[]) => {
    // Only 1 workshops can't cause any kind of conflict, so return false
    if (workshops.length === 1) return false;

    return findConflicts(workshops, courses);
};

const replaceStringByCourseName = (courses: List[], message: string, courseId?: number): string => {
    const course = courses.find((c) => c.id === courseId);
    return course ? message.replace('%s', course.name) : message;
};

/**
 * Compare 2 workshops according to the order of their corresponding course order.
 */
const compareWorkshopListByCourseOrder = (w1: Workshop, w2: Workshop, courseList: List[]) =>
    courseList.findIndex((c) => c.id === w1.course.id) - courseList.findIndex((c) => c.id === w2.course.id);

/**
 * This method compare if a Workshop has meetings with date conflicts with others.
 * - Conflict 1: When startDate is lower than other workshop meeting startDate
 * - Conflict 2: When startDate or endDate overlap another workshop meeting date
 *
 * The parameters 'strings' and 'courses' is used to replace the course name in conflict message alert.
 *
 * @param strings Object of LocaleData
 * @param courses List of courses
 * @param workshop Current Workshop
 * @param list All Workshop list to compare
 */
const compareWorkshops = (
    strings: LocaleData,
    courses: List[],
    workshop?: Workshop,
    list?: Workshop[],
): (Workshop & { conflicts: string[] }) | null => {
    if (workshop && list) {
        list.sort((w1: Workshop, w2: Workshop) => compareWorkshopListByCourseOrder(w1, w2, courses));

        const index = list.indexOf(workshop);

        if (index > -1) {
            const listToCompare = list.slice(0, index);
            const conflicts: string[] = [];

            workshop.meetings
                .filter((meeting: WorkshopMeeting) => meeting.startDate && meeting.endDate)
                .map((it: WorkshopMeeting) => {
                    it.hasConflict = false;
                    listToCompare.forEach((workshop2) => {
                        const hasOverlap = workshop2.meetings
                            .filter((m1: WorkshopMeeting) => m1.startDate && m1.endDate)
                            .some((meeting1: WorkshopMeeting) => {
                                return areIntervalsOverlapping(
                                    { start: it.startDate, end: it.endDate },
                                    { start: meeting1.startDate, end: meeting1.endDate },
                                );
                            });
                        if (hasOverlap) {
                            it.hasConflict = true;
                            conflicts.push(replaceStringByCourseName(courses, strings.WORKSHOP_SAME_DAY_AND_HOUR_ANOTHER, workshop2.course.id));
                        } else {
                            const startDateList = _.pluck(workshop2.meetings, 'startDate');
                            const dateIndex = startDateList.findIndex((value1) => it.startDate < value1);
                            if (dateIndex !== -1) {
                                it.hasConflict = true;
                                conflicts.push(replaceStringByCourseName(courses, strings.WORKSHOP_BEFORE_ANOTHER, workshop2.course.id));
                            }
                        }
                    });
                });

            return {
                ...workshop,
                conflicts,
            };
        }
        return {
            ...workshop,
            conflicts: [],
        };
    }
    return null;
};

const getWorkshopByCourseId = (courseId: number | undefined, workshops: Workshop[] | undefined): Workshop | undefined => {
    if (!workshops || workshops.length === 0) {
        return undefined;
    }

    return workshops.find((w: Workshop) => w.course.id === courseId);
};

const isWorkshopConfigured = (workshops: Workshop[] | undefined, courseId: number | undefined): boolean => {
    const workshop: Workshop | undefined = getWorkshopByCourseId(courseId, workshops);

    if (!workshop) return false;

    return Boolean(workshop.meetings && workshop.meetings.length > 0);
};

export { programHasWorkshopConflict, compareWorkshops, isWorkshopConfigured, getWorkshopByCourseId };
